import { ActionList } from '@/components/DragAndDrop';
import DnDBlockItem from '@/components/DragAndDrop/DnDBlockItem';
import EmptyActionList from '@/components/EmptyActionList';
import { IconChevronDown } from '@/theme/icons';
import { Action, RecurringWeekContext } from '@/types/actions';
import { Block } from '@/types/block';
import { DnDBlockItemProps } from '@/types/dragAndDrop';
import { getActionsByBlockId } from '@/utils/action';
import rem from '@/utils/rem';
import { Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Flex, Text } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';

type BlockListCompletedProps = {
  className?: string;
  blocks: Block[];
  allActionsOrderedByCreatedAt: Action[];
  onSelectedBlock: React.Dispatch<React.SetStateAction<Block | null>>;
  collapsedByDefault?: boolean;
  disableRecurringStatusChanges?: boolean;
  recurringWeekContext?: RecurringWeekContext | null;
} & Pick<
  DnDBlockItemProps,
  | 'onCompleteBlock'
  | 'openSetUncompletedActionsModal'
  | 'onUncompleteBlock'
  | 'openEditBlockModal'
  | 'onClickRemoveFromBlock'
  | 'onDeleteBlock'
  | 'openDuplicateBlockModal'
>;

function BlockListCompleted({
  blocks,
  className,
  allActionsOrderedByCreatedAt,
  onSelectedBlock,
  collapsedByDefault = false,
  disableRecurringStatusChanges = false,
  recurringWeekContext = null,
  ...DnDBlockItemProps
}: BlockListCompletedProps) {
  const { t } = useTranslation('BlockListCompleted');
  return (
    <Accordion width="full" marginTop={rem(16)} allowToggle reduceMotion>
      <AccordionItem>
        <AccordionButton className={className} gap={rem(4)} padding="0">
          <Flex flex="1" textAlign="left">
            <Text textStyle="xsmall" color="text-secondary" textTransform="uppercase">
              {t('labels.completedBlocks', { count: blocks.length })}
            </Text>
          </Flex>
          <AccordionIcon as={IconChevronDown} width={rem(14)} height={rem(14)} color="text-primary" />
        </AccordionButton>
        <AccordionPanel marginTop={rem(8)} padding={0} paddingBottom={4}>
          <ActionList
            isEmpty={isEmpty(blocks)}
            emptyListComponent={<EmptyActionList message={t('labels.noCompletedBlocks')} />}
          >
            {blocks.map((block, index) => (
              <DnDBlockItem
                key={block.id}
                actions={getActionsByBlockId(block.id, allActionsOrderedByCreatedAt)}
                block={block}
                index={index}
                weeklyPlanId=""
                onCompleteBlock={DnDBlockItemProps.onCompleteBlock}
                openSetUncompletedActionsModal={DnDBlockItemProps.openSetUncompletedActionsModal}
                onUncompleteBlock={DnDBlockItemProps.onUncompleteBlock}
                onSelectBlock={() => onSelectedBlock(block)}
                onClickRemoveFromBlock={DnDBlockItemProps.onClickRemoveFromBlock}
                withScheduleButton
                openEditBlockModal={DnDBlockItemProps.openEditBlockModal}
                onDeleteBlock={DnDBlockItemProps.onDeleteBlock}
                openDuplicateBlockModal={DnDBlockItemProps.openDuplicateBlockModal}
                collapsedByDefault={collapsedByDefault}
                disableRecurringStatusChanges={disableRecurringStatusChanges}
                recurringWeekContext={recurringWeekContext}
              />
            ))}
          </ActionList>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}

export default BlockListCompleted;
